/**
 * Frontend Logs Event Filter
 *
 * This module controls which analytics events should also be sent to
 * `/common/frontend-logs/` via `postFrontendLogs`.
 *
 * Default behavior is **opt-in** (no events are logged) until you configure it.
 */

/**
 * @typedef {"allowlist" | "all" | "none"} FrontendLogsMode
 */

/** @type {FrontendLogsMode} */
let mode = "none";

/** @type {Set<string>} */
let allowlist = new Set();

/**
 * Replace the allowlist and enable allowlist mode.
 *
 * @param {string[]} eventNames - Event names to allow for backend logging
 */
export const setFrontendLogsAllowlist = (eventNames = []) => {
	mode = "allowlist";
	allowlist = new Set((eventNames || []).filter(Boolean));
};

/**
 * Add events to the existing allowlist (keeps allowlist mode enabled).
 *
 * @param {string[]} eventNames - Event names to add
 */
export const addFrontendLogsEvents = (eventNames = []) => {
	mode = "allowlist";
	(eventNames || []).filter(Boolean).forEach((e) => allowlist.add(e));
};

/**
 * Remove events from the allowlist.
 *
 * @param {string[]} eventNames - Event names to remove
 */
export const removeFrontendLogsEvents = (eventNames = []) => {
	(eventNames || []).filter(Boolean).forEach((e) => allowlist.delete(e));
};

/**
 * Set global logging mode.
 *
 * - "all": log every event
 * - "none": log no events
 * - "allowlist": log only allowlisted events
 *
 * @param {FrontendLogsMode} nextMode
 */
export const setFrontendLogsMode = (nextMode) => {
	if (nextMode === "all" || nextMode === "none" || nextMode === "allowlist") {
		mode = nextMode;
	}
};

/**
 * Decide if an event should be sent to backend frontend logs.
 *
 * @param {string} eventName
 * @returns {boolean}
 */
export const shouldLogFrontendEvent = (eventName) => {
	if (!eventName) {
		return false;
	}
	if (mode === "all") {
		return true;
	}
	if (mode === "none") {
		return false;
	}
	return allowlist.has(eventName);
};

export const getFrontendLogsConfig = () => ({
	mode,
	allowlist: Array.from(allowlist),
});
